import { useState, useEffect, useCallback } from "react";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import {
  applyConsent,
  getConsent,
  setConsent,
  OPEN_CONSENT_EVENT,
  type ConsentChoices,
} from "@/lib/consent";

const CookieConsent = () => {
  const lang = useLanguage();
  const t = uiT(lang);
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choices, setChoices] = useState<ConsentChoices>({ analytics: false, marketing: false });

  useEffect(() => {
    if (!getConsent()) setVisible(true);
    const open = () => {
      const c = getConsent();
      setChoices(c ? { analytics: c.analytics, marketing: c.marketing } : { analytics: false, marketing: false });
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, open);
  }, []);

  const decide = useCallback((c: ConsentChoices) => {
    setConsent(c);
    applyConsent(c);
    setVisible(false);
    setShowSettings(false);
  }, []);

  if (!visible) return null;

  const btn =
    "flex-1 sm:flex-none rounded-lg text-sm font-semibold px-5 bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground border border-primary-foreground/25";

  return (
    <div
      role="region"
      aria-label={t("cookieTitle")}
      className="fixed bottom-0 inset-x-0 z-[9999] p-3 sm:p-4 pointer-events-none"
    >
      <div className="container mx-auto max-w-5xl pointer-events-auto">
        <div className="bg-primary text-primary-foreground rounded-2xl shadow-2xl border border-primary-foreground/10 p-4 sm:p-5">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="rounded-xl bg-primary-foreground/10 p-2.5 mt-0.5 shrink-0">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <p className="text-sm sm:text-base font-medium leading-snug">{t("cookieTitle")}</p>
                <p className="text-xs sm:text-sm text-primary-foreground/70 leading-relaxed">
                  {t("cookieDesc")} {t("readOur")}{" "}
                  <a
                    href={lang === "es" ? "/es/politica-de-privacidad-qbd-group" : "/en/privacy-policy"}
                    className="underline underline-offset-2"
                  >
                    {t("cookiePrivacy")}
                  </a>{" "}
                  {t("and")}{" "}
                  <a
                    href={lang === "es" ? "/es/declaracion-de-cookies" : "/en/cookie-statement"}
                    className="underline underline-offset-2"
                  >
                    {t("cookieCookiePolicy")}
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto shrink-0">
              <Button size="sm" className={btn} onClick={() => decide({ analytics: true, marketing: true })}>
                {t("cookieAccept")}
              </Button>
              <Button size="sm" className={btn} onClick={() => decide({ analytics: false, marketing: false })}>
                {t("cookieReject")}
              </Button>
              <Button
                size="sm"
                className={btn}
                aria-expanded={showSettings}
                onClick={() => setShowSettings((s) => !s)}
              >
                {t("cookieSettings")}
              </Button>
            </div>
          </div>

          {showSettings && (
            <div className="mt-4 pt-4 border-t border-primary-foreground/15 flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="flex-1 grid sm:grid-cols-2 gap-3">
                {(["analytics", "marketing"] as const).map((k) => (
                  <label key={k} className="flex items-start gap-3 rounded-xl bg-primary-foreground/5 p-3 cursor-pointer">
                    <Switch
                      checked={choices[k]}
                      onCheckedChange={(v) => setChoices((c) => ({ ...c, [k]: v }))}
                      aria-label={t(k === "analytics" ? "cookieAnalytics" : "cookieMarketing")}
                    />
                    <span>
                      <span className="block text-sm font-semibold">
                        {t(k === "analytics" ? "cookieAnalytics" : "cookieMarketing")}
                      </span>
                      <span className="block text-xs text-primary-foreground/70">
                        {k === "analytics" ? "GA4, Ahrefs" : "HubSpot, LinkedIn, Google Ads, Leadfeeder"}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <Button size="sm" className={btn} onClick={() => decide(choices)}>
                {t("cookieSave")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
